/**
 * FRONT-2382 — pure disabled+reason gate logic for the "Design direction"
 * section, same shape as `smart-tagging-run-gate.ts`/`finish-now-gate.ts`:
 * `disabled` is DERIVED from a reason string here, in one place the section
 * and its test both consult, so a control can never go dark without saying
 * why (CLAUDE.md's disabled-control rule) and a future extra reason cannot
 * be added without a matching sentence.
 *
 * Four independent controls, four independent gates — deliberately not
 * folded into one function, since they answer unrelated questions ("can I
 * ask the web for ideas", "can I pick this card", "can I add this file",
 * "can I submit the form right now") and combining them would just recreate
 * the four-reasons-one-expression bug this shape exists to avoid.
 */

import { MAX_DESIGN_PICKS, MAX_DESIGN_UPLOADS } from './design-direction';

export interface DesignGateState {
  disabled: boolean;
  /** Present whenever `disabled` is true, absent otherwise — rendered as
   *  the control's hover tooltip AND as visible text underneath it, since a
   *  hover hint alone is no explanation on a touch screen. */
  reason?: string;
}

export interface SuggestGateInput {
  /** The New-project form's name field, as typed (untrimmed is fine). */
  name: string;
  /** The upfront-context field, as typed. */
  context: string;
  /** A suggestions request (first batch or "More suggestions") is in flight. */
  suggesting: boolean;
}

/** "Suggest directions from the web" / "More suggestions". */
export function suggestGateState({ name, context, suggesting }: SuggestGateInput): DesignGateState {
  if (suggesting) return { disabled: true, reason: 'Getting suggestions from the web…' };
  if (!name.trim() && !context.trim()) {
    return { disabled: true, reason: 'Add a name or some context first' };
  }
  return { disabled: false };
}

export interface PickGateInput {
  /** This specific card is already picked. Unpicking always stays available
   *  — the cap only ever blocks adding a NEW pick, never removing one. */
  picked: boolean;
  /** How many suggestions are picked right now, across every batch fetched
   *  so far (not just the ones on screen). */
  pickedCount: number;
}

/** Clicking a suggestion card to pick (or unpick) it. */
export function pickGateState({ picked, pickedCount }: PickGateInput): DesignGateState {
  if (picked) return { disabled: false };
  if (pickedCount >= MAX_DESIGN_PICKS) {
    return {
      disabled: true,
      reason: `You've picked ${MAX_DESIGN_PICKS} — unpick one to choose another`,
    };
  }
  return { disabled: false };
}

export interface UploadGateInput {
  /** Uploads already staged PLUS ones still mid-upload — a slot is spent
   *  the moment a file is queued, not just once it lands, so dropping a
   *  burst of 8 files can't slip past the cap while requests are in flight. */
  uploadCount: number;
}

/** Adding a reference image (the drop/pick area). */
export function uploadGateState({ uploadCount }: UploadGateInput): DesignGateState {
  if (uploadCount >= MAX_DESIGN_UPLOADS) {
    return {
      disabled: true,
      reason: `You've added ${MAX_DESIGN_UPLOADS} — remove one to add another`,
    };
  }
  return { disabled: false };
}

export interface CreateGateInput {
  /** At least one design-direction reference upload is still queued or
   *  mid-flight (see `ReferenceImagesField`'s `UploadInFlight` list, now
   *  lifted to `NewProjectView` — FRONT-2382 review #3). Only this reason is
   *  modelled here: an empty name or an in-flight create request already
   *  disable the button today with no explanation required of THIS gate,
   *  so this input intentionally does not fold those in — doing so would
   *  mean returning `disabled: true` with no `reason` for them, breaking
   *  `DesignGateState`'s own "present whenever disabled is true" contract. */
  uploading: boolean;
}

/** "Create project" (and its Cmd/Ctrl+Enter shortcut) — blocked while a
 *  reference image is still uploading, so a submit can never beat its own
 *  upload to the create request and ship a project missing an image that
 *  still looked staged on screen. */
export function createGateState({ uploading }: CreateGateInput): DesignGateState {
  if (uploading) return { disabled: true, reason: 'Wait for the images to finish uploading' };
  return { disabled: false };
}
